import firebaseApp from "../firebase/credenciales";
import { getFirestore, collection, doc, setDoc, updateDoc, getDocs, query, where } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { initializeApp, getApps } from "firebase/app";
import { firebaseWebConfig } from "../config/firebaseEnv";
import Swal from 'sweetalert2';
import escribirLog from "./escribirLog";

// Configuración de Firebase (Debe coincidir con la de credenciales.js)
const firebaseConfig = firebaseWebConfig;

// Función para obtener o crear una instancia secundaria de Firebase
// Esto evita que createUserWithEmailAndPassword cierre la sesión del admin
const getSecondaryAuth = () => {
  const appName = "secondaryApp";
  let secondaryApp;

  const apps = getApps();
  const existingApp = apps.find(app => app.name === appName);

  if (existingApp) {
    secondaryApp = existingApp;
  } else {
    secondaryApp = initializeApp(firebaseConfig, appName);
  }

  return getAuth(secondaryApp);
};

// ✅ Función refactorizada con lógica "Firestore Primero" y "Auth Secundaria"
async function añadirProducto(infoProducto, autor, baseDatos) {
  // 1. Validaciones previas
  if (!infoProducto.email || !infoProducto.dni || !infoProducto.name) {
    Swal.fire({
      position: 'top-end',
      icon: 'error',
      title: 'Faltan datos obligatorios',
      text: 'Completa el correo electrónico, DNI y nombre para poder crear el usuario.',
      showConfirmButton: false,
      timer: 3000
    });
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(infoProducto.email)) {
    Swal.fire({
      position: 'top-end',
      icon: 'error',
      title: 'Correo electrónico inválido',
      text: 'Ingresa un correo electrónico válido.',
      showConfirmButton: false,
      timer: 3000
    });
    return false;
  }

  if (!/^\d{8,9}$/.test(infoProducto.dni)) {
    Swal.fire({
      position: 'top-end',
      icon: 'error',
      title: 'DNI incorrecto',
      text: 'El DNI debe tener 8 o 9 dígitos numéricos.',
      showConfirmButton: false,
      timer: 3000
    });
    return false;
  }

  const db = getFirestore(firebaseApp);
  const secondaryAuth = getSecondaryAuth();

  try {
    const rucEmpresa = String(infoProducto.rucEmpresa || "").trim();
    const dniNormalizado = String(infoProducto.dni || "").trim().toUpperCase();
    const emailNormalizado = String(infoProducto.email || "").trim().toLowerCase();

    if (rucEmpresa && (dniNormalizado || emailNormalizado)) {
      let bloqueado = false;

      if (dniNormalizado) {
        const qDni = query(
          collection(db, "pl_lista_negra"),
          where("ruc_empresa", "==", rucEmpresa),
          where("dni", "==", dniNormalizado),
          where("estado", "==", "active")
        );
        const snapDni = await getDocs(qDni);
        bloqueado = !snapDni.empty;
      }

      if (!bloqueado && emailNormalizado) {
        const qEmail = query(
          collection(db, "pl_lista_negra"),
          where("ruc_empresa", "==", rucEmpresa),
          where("email", "==", emailNormalizado),
          where("estado", "==", "active")
        );
        const snapEmail = await getDocs(qEmail);
        bloqueado = !snapEmail.empty;
      }

      if (bloqueado) {
        Swal.fire({
          position: 'top-end',
          icon: 'error',
          title: 'Contratación bloqueada',
          text: 'El trabajador se encuentra en lista negra activa y no puede ser contratado.',
          showConfirmButton: false,
          timer: 3500
        });
        return false;
      }
    }

    // Normalización estricta de correo y DNI
    infoProducto.email = emailNormalizado;
    infoProducto.dni = dniNormalizado;
    infoProducto.id = dniNormalizado;

    // 2. CREACIÓN EN FIREBASE AUTH PRIMERO
    let uid = "";
    let isExistingAuthUser = false;
    const docRef = doc(collection(db, baseDatos), dniNormalizado);

    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, emailNormalizado, dniNormalizado);
      uid = userCredential.user.uid;
      console.log(`✅ Usuario creado en Auth: ${uid}`);

      // Enviar correo de verificación
      try {
        await sendEmailVerification(userCredential.user);
      } catch (verifyErr) {
        console.warn("No se pudo enviar correo de verificación:", verifyErr.message);
      }
    } catch (authError) {
      if (authError.code === 'auth/email-already-in-use') {
        console.warn('⚠️ El correo ya existe en Auth. Verificando si corresponde al mismo DNI en Firestore...');

        // Verificar si el documento del usuario ya existe en Firestore para este DNI
        const existingDocSnap = await getDocs(query(collection(db, baseDatos), where("dni", "==", dniNormalizado)));

        if (existingDocSnap.empty) {
          // El correo está usado por OTRO DNI/usuario en Auth
          Swal.fire({
            position: 'top-end',
            icon: 'error',
            title: 'Correo ya registrado',
            text: `El correo ${emailNormalizado} ya se encuentra registrado en el sistema bajo otra cuenta. Usa un correo distinto.`,
            showConfirmButton: true
          });
          return false;
        }

        isExistingAuthUser = true;
      } else {
        // Re-lanzar error para que se capture en el bloque principal SIN guardar en Firestore
        throw authError;
      }
    }

    // 3. FIRESTORE SEGUNDO: Solo si Auth tuvo éxito o fue actualización válida
    if (uid) {
      infoProducto.firebaseUid = uid;
    }

    await setDoc(docRef, infoProducto, { merge: true });
    console.log(`✅ Usuario guardado/actualizado en Firestore: ${dniNormalizado}`);

    escribirLog("Usuario Guardado en Firestore", infoProducto, autor);

    Swal.fire({
      position: 'top-end',
      icon: 'success',
      title: isExistingAuthUser ? 'Perfil Actualizado' : '¡Usuario creado exitosamente!',
      text: isExistingAuthUser
        ? 'El usuario ya existía en el sistema. Se han actualizado sus datos.'
        : 'Se ha enviado un correo de verificación. El usuario ha sido registrado en el sistema.',
      showConfirmButton: false,
      timer: 4000
    });

    return true; // ✅ Éxito total

  } catch (error) {
    console.error('Error en el proceso de creación:', error);

    let errorTitle = 'Error';
    let errorText = error.message;

    if (error.code === 'auth/weak-password') {
      errorTitle = 'Contraseña no segura';
      errorText = 'La contraseña (DNI) debe tener al menos 6 caracteres numéricos.';
    } else if (error.code === 'auth/invalid-email') {
      errorTitle = 'Correo no válido';
      errorText = 'El formato del correo electrónico ingresado no es válido para la autenticación.';
    }

    Swal.fire({
      position: 'top-end',
      icon: 'error',
      title: errorTitle,
      text: errorText,
      showConfirmButton: false,
      timer: 3500
    });

    escribirLog("Error Crítico al Crear Usuario", { ...infoProducto, error: error.message }, autor);
    return false; // ❌ Error sin guardar documento en Firestore
  }
}

export default añadirProducto;
